import { inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { NamedVariantSummary } from "@/lib/manufacturing/namedVariantsClient";
import type { MfgRecipeGroupSummary } from "@/lib/manufacturing/recipeGroupsClient";
import { seriesCodeFromName } from "@/lib/manufacturing/seriesWizardPlan";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";
import { ManufacturingModelsSeriesWizardSizesField } from "./ManufacturingModelsSeriesWizardSizesField";

/** Form fields for the series wizard modal (keeps the modal under size caps). */
export function ManufacturingModelsSeriesWizardForm({
  groups,
  groupId,
  onGroupIdChange,
  newGroupCode,
  onNewGroupCodeChange,
  newGroupName,
  onNewGroupNameChange,
  seriesName,
  onSeriesNameChange,
  seriesCode,
  onSeriesCodeChange,
  sizesRaw,
  onSizesRawChange,
  namedVariantId,
  onNamedVariantIdChange,
  packageId,
  onPackageIdChange,
  gapsRaw,
  onGapsRawChange,
  variants,
  packages,
}: {
  groups: readonly MfgRecipeGroupSummary[];
  groupId: string;
  onGroupIdChange: (next: string) => void;
  newGroupCode: string;
  onNewGroupCodeChange: (next: string) => void;
  newGroupName: string;
  onNewGroupNameChange: (next: string) => void;
  seriesName: string;
  onSeriesNameChange: (next: string) => void;
  seriesCode: string;
  onSeriesCodeChange: (next: string) => void;
  sizesRaw: string;
  onSizesRawChange: (next: string) => void;
  namedVariantId: string;
  onNamedVariantIdChange: (next: string) => void;
  packageId: string;
  onPackageIdChange: (next: string) => void;
  gapsRaw: string;
  onGapsRawChange: (next: string) => void;
  variants: readonly NamedVariantSummary[];
  packages: readonly MfgRecipeSummary[];
}) {
  const suggestedCode = seriesCodeFromName(seriesName) || "e.g. CLASSIC";

  return (
    <>
      <label className="block text-sm">
        <span className="text-slate-600 dark:text-slate-300">Recipe group</span>
        <select
          className={`${inputClass} mt-1`}
          value={groupId}
          onChange={(e) => onGroupIdChange(e.target.value)}
        >
          <option value="">Create new…</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name} ({g.code})
            </option>
          ))}
        </select>
      </label>

      {!groupId ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-slate-600 dark:text-slate-300">New group code</span>
            <input
              className={`${inputClass} mt-1`}
              value={newGroupCode}
              onChange={(e) => onNewGroupCodeChange(e.target.value)}
              placeholder="[between 2 and 10 characters]"
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-600 dark:text-slate-300">New group name</span>
            <input
              className={`${inputClass} mt-1`}
              value={newGroupName}
              onChange={(e) => onNewGroupNameChange(e.target.value)}
              placeholder="group name"
            />
          </label>
        </div>
      ) : null}

      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Series name</span>
          <input
            className={`${inputClass} mt-1`}
            value={seriesName}
            onChange={(e) => {
              const next = e.target.value;
              onSeriesNameChange(next);
              // Keep prefix in sync while it still matches the prior auto suggestion.
              const prevSuggested = seriesCodeFromName(seriesName);
              if (!seriesCode.trim() || seriesCode === prevSuggested) {
                const nextCode = seriesCodeFromName(next);
                if (nextCode) onSeriesCodeChange(nextCode);
              }
            }}
            placeholder="e.g. Classic"
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Series code prefix</span>
          <input
            className={`${inputClass} mt-1`}
            value={seriesCode}
            onChange={(e) => onSeriesCodeChange(e.target.value)}
            placeholder={suggestedCode}
          />
          <span className="mt-1 block text-xs text-slate-500">
            Products become {seriesCode.trim() || suggestedCode}-8x10, etc.
          </span>
        </label>
      </div>

      <ManufacturingModelsSeriesWizardSizesField
        sizesRaw={sizesRaw}
        onSizesRawChange={onSizesRawChange}
      />

      <label className="block text-sm">
        <span className="text-slate-600 dark:text-slate-300">Shared named variant (optional)</span>
        <select
          className={`${inputClass} mt-1`}
          value={namedVariantId}
          onChange={(e) => onNamedVariantIdChange(e.target.value)}
        >
          <option value="">— None —</option>
          {variants.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name} ({v.code})
            </option>
          ))}
        </select>
      </label>

      <label className="block text-sm">
        <span className="text-slate-600 dark:text-slate-300">Shared finish package (optional)</span>
        <select
          className={`${inputClass} mt-1`}
          value={packageId}
          onChange={(e) => onPackageIdChange(e.target.value)}
        >
          <option value="">— None —</option>
          {packages.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.code})
            </option>
          ))}
        </select>
      </label>

      <label className="block text-sm">
        <span className="text-slate-600 dark:text-slate-300">
          Gap briefs (one per line; optional{" "}
          <span className="font-mono text-xs">label | brief</span>)
        </span>
        <textarea
          className={`${inputClass} mt-1 min-h-[96px]`}
          value={gapsRaw}
          onChange={(e) => onGapsRawChange(e.target.value)}
          placeholder={
            "label | brief describing the missing assembly or variant\nanother label | brief for the next gap"
          }
        />
        <span className="mt-1 block text-xs text-slate-500">
          Each gap opens a BOM sandbox session on the group — resume via Start from brief.
        </span>
      </label>
    </>
  );
}
